import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Injector, OnDestroy,
  OnInit
} from '@angular/core';
import { CompactType, DisplayGrid, GridsterConfig, GridType, GridsterComponentInterface, GridsterItemComponentInterface, GridsterItemComponent } from 'angular-gridster2';
import { ELEMENT_PARAMETERS, ELEMENT_STATE_NAME, LandingPageTile } from '../shared/tiles/home-tile.model';
import { cloneDeep } from 'lodash-es';
import { filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { SidebarService } from '../shared/sidebar/sidebar.service';
import { StateService } from '../shared/state/state.service';
import { combineLatest, from, Observable, Subject } from 'rxjs';
import { SidebarState } from '../shared/state/sidebar.state';
import { availableTiles, maxCols, maxRows } from '../shared/tiles/tile-config';
import { BaseComponent } from '../components/general/base/base.component';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';
import { DomSanitizer } from '@angular/platform-browser';
import { getGa } from '../../declarations/ga';
import { GridsterItem } from 'angular-gridster2/lib/gridsterItem.interface';
import { ActivatedRoute } from '@angular/router';
import { AppMessageService } from '../shared/app-message/app-message.service';
import { AppMessage } from '../shared/app-message/app-message';
import { ReminderService } from '../reminder/reminder-service/reminder.service';
import { MapService } from '../shared/map/map.service';
import { LinkingService } from '../linking/linking.service';
import { preventDisasterButton } from '~app/shared/utils';
import { AlarmService } from '../alarm/alarm.service';
import { isAlarmActiveForUser } from '../alarm/alarm.utils';
import { MeasurementsService } from '../measurements-miso/measurements.service';



interface OldDashboardItemState {
  id: any;
  x: number;
  y: number;
  cols: number;
  rows: number;
}
@Component({
  selector: 'qm-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent extends BaseComponent implements OnInit, OnDestroy {

  public options: GridsterConfig;
  public sidebarHidden = true;
  public dashboard: Array<LandingPageTile> = [];
  public sidebarPanels: Array<LandingPageTile> = availableTiles;
  public isMapReady = false;

  public activeAlarmsLength = combineLatest([
    this.auth.login$,
    this.alarmService.filteredAlarms$,
  ]).pipe(
    // TODO: Generalize this feature for tenants other than VR (currently has inconsistent alarm counts)
    mergeMap(([login, alarms]) => login.tenant === 2 ? from(alarms) : from([])),
    map(alarms => alarms.length),
  );

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();
  private injectorMap: Map<string, Injector> = new Map();
  private restorableDashboardItems: LandingPageTile[] = [];
  private oldPositions: Map<string, OldDashboardItemState>;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private stateService: StateService,
    private auth: AuthService,
    private sanitizer: DomSanitizer,
    private injector: Injector,
    private router: ActivatedRoute,
    private mapService: MapService,
    private readonly linkingService: LinkingService,
    private reminderService: ReminderService, // injected for instantiating the service on start
    protected appMessageService: AppMessageService,
    protected sidebarService: SidebarService,
    private alarmService: AlarmService,
    private measurementService: MeasurementsService
  ) {
    super();
    this.name = 'HomeComponent';
  }

  private static getComponentName(): string {
    return 'Home';
  }

  ngOnInit() {
    this.router.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe(async params => {
      //// VIVO asset is linked against REGO
      if (params['id']) {
        for (const linking of await this.linkingService.getLinkingsForSourceId(params['id'])) {
          this.mapService.showVivoAssetOnMap(linking.regoId);
        }
      }
    });

    const gridsterCb = (gridster: GridsterComponentInterface) => {
      this.afterGridsterInitialized();
    };

    this.options = {
      initCallback: gridsterCb,
      gridType: GridType.Fixed,
      compactType: CompactType.None,
      itemChangeCallback: (o: GridsterItem) => {
        this.sidebarService.savePanelSetup(o as LandingPageTile);
      },
      itemResizeCallback: (item: GridsterItem, component: GridsterItemComponent) => {
        if (item?.onResize) {
          item.onResize(component.width, component.height);
        }
      },
      margin: 0,
      outerMargin: true,
      outerMarginTop: null,
      outerMarginRight: null,
      outerMarginBottom: null,
      outerMarginLeft: null,
      mobileBreakpoint: 640,
      minCols: 1,
      maxCols: maxCols,
      minRows: 1,
      maxRows: maxRows,
      maxItemCols: maxCols,
      minItemCols: 1,
      maxItemRows: maxRows,
      minItemRows: 1,
      maxItemArea: 5500,
      minItemArea: 1,
      defaultItemCols: 1,
      defaultItemRows: 1,
      keepFixedHeightInMobile: false,
      keepFixedWidthInMobile: false,
      scrollSensitivity: 10,
      scrollSpeed: maxRows,
      enableEmptyCellClick: false,
      enableEmptyCellContextMenu: false,
      enableEmptyCellDrop: false,
      enableEmptyCellDrag: false,
      emptyCellDragMaxCols: 100,
      emptyCellDragMaxRows: 50,
      ignoreMarginInRow: false,
      draggable: {
        dragHandleClass: 'drag-handler',
        ignoreContent: true,
        enabled: true,
      },
      resizable: {
        enabled: true,
      },
      swap: false,
      pushItems: false,
      disablePushOnDrag: false,
      disablePushOnResize: false,
      pushDirections: { north: true, east: true, south: true, west: true },
      pushResizeItems: false,
      displayGrid: DisplayGrid.None,
      disableWindowResize: false,
      disableWarnings: false,
      scrollToNewItems: false
    };

    preventDisasterButton();
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private afterGridsterInitialized() {
    console.log('Gridster initialized, getting HomeComponent state');
    const state = this.stateService.getPartOfState(HomeComponent.getComponentName());
    console.log('Init sidebar with state', state);
    const panelsToRestore = state.visiblePanels.filter(panel => {
      return !this.injectablePanelId(panel);
    });
    for (const panel of panelsToRestore) {
      const p = this.getPanelById(panel);
      const pp = this.sidebarService.loadPanelSetup(panel);
      if (p) {
        if (pp) {
          Object.assign(p, pp);
        }
        this.toggleTileVisible(p);
      }
    }
    this.sidebarService.sidebarChanged$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(sidebarChange => {
      const { panelId, params } = sidebarChange;
      const panel = this.getPanelById(panelId);
      if (!this.isTileVisible(panelId)) {
        panel.params = params;
        this.toggleTileVisible(panel);
        this.changeDetector.markForCheck();
      }
      if (panel.onShow) {
        // allow Observables to settle first
        setTimeout(() => {
          panel.onShow();
        }, 0);
      }
    });

    this.sidebarService.titleChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(data => {
      console.log('TITLE', data);
      const dbTile = this.getDashboardTile(data.panel);
      if (dbTile) {
        dbTile.title = data.title;
        dbTile.titleTemplate = data.template;
      }
      this.changeDetector.markForCheck();
    });

    this.sidebarService.requestStream$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(data => {
      console.log('MAXIMIZE PANEL', data.panel);
      const tile = this.getDashboardTile(data.panel);
      if (tile) {
        if (data.maximize) {
          this.maximizeTile({
            preventDefault: () => {
            }, stopPropagation: () => {
            }
          }, tile);
        } else {
          this.restoreTile({
            preventDefault: () => {
            }, stopPropagation: () => {
            }
          }, tile);
        }
      }
    });

    this.calculateGridDimensions();
    this.changedOptions();
  }

  private calculateGridDimensions(): void {
    const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    const viewWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const emInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const gridHeight = viewHeight - 2.8 * emInPixels;
    const gridWidth = viewWidth - 6.1 * emInPixels;
    console.log('Grid dimensions', gridWidth, gridHeight);
    this.options.fixedColWidth = Math.floor(gridWidth / maxCols);
    this.options.fixedRowHeight = Math.floor(gridHeight / maxRows);
    console.log('Column dimensions', this.options.fixedColWidth, this.options.fixedRowHeight);
  }

  private getPanelById(id: string): LandingPageTile {
    let searchValue;
    const injectableId = this.injectablePanelId(id);
    if (injectableId) {
      searchValue = injectableId;
    } else {
      searchValue = id;
    }
    const r = this.sidebarPanels.find(o => {
      return o.panel === searchValue;
    });
    if (r) {
      if (r.panel === injectableId) {
        console.log(`Construct Injectable panel ${injectableId}`);
        const newPanel = cloneDeep(r);
        newPanel.panel = id;
        return newPanel;
      } else {
        return r;
      }
    } else {
      console.log('No panel found. There must be an error somewhere.');
      return undefined;
    }
  }

  public getTenant() {
    return this.auth.getTenant();
  }

  public changedOptions(): void {
    if (this.options.api && this.options.api.optionsChanged) {
      this.options.api.optionsChanged();
    }
  }

  public removeTile($event, item: LandingPageTile): void {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (item.onHide) {
      item.onHide();
    }
    if (item.maximized) {
      this.dashboard = this.restorableDashboardItems;
      this.dashboard.forEach(dItem => this.restoreDashboardItem(dItem));
      this.options.api.optionsChanged();
    }
    console.log(item.panel);
    if (this.injectablePanelId(item.panel)) {
      this.sidebarService.removePanelSetup(item);
      this.stateService.removePartOfState(item.panel);
      this.mapService.changeCurrentFeature(null);
    }
    this.dashboard.splice(this.dashboard.indexOf(item), 1);
    this.updateState();
  }

  public maximizeTile($event, item: LandingPageTile): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.oldPositions = new Map<string, OldDashboardItemState>();
    this.restorableDashboardItems = [];

    this.dashboard.forEach(dItem => {
      // save current position of all items
      this.oldPositions.set(dItem.panel, {
        x: dItem.x,
        y: dItem.y,
        cols: dItem.cols,
        rows: dItem.rows,
        id: dItem.panel
      });
      dItem.x = 0;
      dItem.y = 0;
      dItem.cols = 0;
      dItem.rows = 0;

      this.restorableDashboardItems.push(dItem);
    });

    this.dashboard = [item];

    item.maximized = true;
    item.x = 0;
    item.y = 0;
    item.cols = maxCols;
    item.rows = maxRows;
    item.resizeEnabled = false;
    item.dragEnabled = false;
    console.log(this.dashboard);
    this.options.api.optionsChanged();
    this.updateState();
  }

  public restoreTile($event, item: LandingPageTile): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.dashboard = this.restorableDashboardItems;
    this.dashboard.forEach(dItem => {
      this.restoreDashboardItem(dItem);
    });
    this.options.api.optionsChanged();
    this.updateState();
  }

  public isTileVisible(itemId: string): boolean {
    // console.log(this.dashboard);
    return this.getDashboardTile(itemId) !== undefined;
  }

  private getDashboardTile(itemId: string): LandingPageTile {
    return this.dashboard.find(o => {
      return o.panel === itemId;
    });
  }

  public toggleTileVisible(item: LandingPageTile): void {
    console.log('TOGGLE', item);
    if (this.options && this.options.api) {
      if (!this.isTileVisible(item.panel)) {
        /**
         * The getNextPossiblePosition method mutates the provided object,
         * so create a copy of it for checking if there is space for new tile
         */
        const blyat: GridsterItem = {
          x: item.x,
          y: item.y,
          cols: item.cols,
          rows: item.rows
        };
        const isSpaceForNewTile = this.options.api.getNextPossiblePosition(blyat);
        if (isSpaceForNewTile) {
          /**
           * This overwrites the previous values
           */
          // const newPos = this.options.api.getFirstPossiblePosition(blyat);
          // item.x = newPos.x;
          // item.y = newPos.y;
          // item.cols = newPos.cols;
          // item.rows = newPos.rows;

          try {
            const ga = getGa();
            ga('send', {
              hitType: 'event',
              eventCategory: 'Sidebar panel opened for tenant ' + this.auth.getTenant(),
              eventAction: item.panel,
              eventLabel: item.panel
            });
          } catch (error) {
          }
          this.dashboard.push(item);
        } else {
          const msgText = 'Yritit avata inforuutua, mutta sille ei ole riittävästi tilaa. '
            + 'Ole hyvä ja sulje tai pienennä avattuja inforuutuja.';
          this.appMessageService.broadcastAlarm(new AppMessage(true, msgText, 'warning', ''));
        }
      } else {
        this.removeTile(null, item);
      }
    }
    this.updateState();
  }

  public getInjectorForPanel = (panel: LandingPageTile): Injector => {
    if (this.injectablePanelId(panel.panel)) {
      // Check if params were changed
      const prevParams = this.injectorMap.get(panel.panel)?.get(ELEMENT_PARAMETERS);
      //// NOTE: refactoring/rework, for example measurement-table injection necessarity?
      if (!prevParams || prevParams?.openTab !== panel.params?.openTab || prevParams?.feature !== panel.params?.feature) {
        this.injectorMap.set(panel.panel, Injector.create({
          providers: [
            { provide: ELEMENT_STATE_NAME, useValue: panel.panel },
            { provide: ELEMENT_PARAMETERS, useValue: panel.params || { openTab: null } }
          ],
          parent: this.injector
        }));
      }
      return this.injectorMap.get(panel.panel);
    } else {
      return this.injector;
    }
  }

  @HostListener('window:resize', ['$event'])
  private onResize(event) {
    console.log('Width: ' + event.target.innerWidth);
    this.calculateGridDimensions();
    this.changedOptions();
    // this.options.api.resize();
  }

  private updateState() {
    this.stateService.updateState(
      HomeComponent.getComponentName(),
      new SidebarState(this.dashboard.map(o => o.panel),
        this.sidebarHidden, null)
    );
  }

  private restoreDashboardItem(dItem: LandingPageTile) {
    const oldPosition = this.oldPositions.get(dItem.panel);
    Object.assign(dItem, oldPosition);
    dItem.maximized = false;
    dItem.dragEnabled = true;
    dItem.resizeEnabled = true;
  }

  private injectablePanelId(panelId: string) {
    if (panelId && panelId.startsWith('ElementInfo')) {
      return 'ElementInfo';
    } else if (panelId && panelId === 'measurements-table') {
      return panelId;
    } else {
      return '';
    }
  }

  public onMapReady() {
    console.warn('Map ready');
    this.isMapReady = true;
  }
}
